import {
  StudentProfile,
  StudentFilters,
  ApiResponse,
  type StudentLessonsByEnrollmentResponse,
  type StudentSiblingSummary,
  type AttendanceHistoryExportRow,
  type AttendanceExportScope,
  type StudentBillingReportResult,
} from '@court-sync/shared';
import type { PaginationState } from '../components/ui/PaginatedTable';
import type { ScheduleResponse } from './schedule';
import { apiGet, apiPost, apiPatch, apiDelete, apiClient } from './apiClient';

export const academyStudentService = {
  async getAcademyStudents(
    academyId: string,
    filters?: StudentFilters
  ): Promise<ApiResponse<StudentProfile[]>> {
    const params: Record<string, string> = {};
    if (filters?.skillLevel) params.skillLevel = filters.skillLevel;
    if (filters?.listRecencyActive !== undefined) {
      params.listRecencyActive = String(filters.listRecencyActive);
    }
    if (filters?.ageMin !== undefined) params.ageMin = String(filters.ageMin);
    if (filters?.ageMax !== undefined) params.ageMax = String(filters.ageMax);
    if (filters?.searchQuery) params.searchQuery = filters.searchQuery;
    if (filters?.sortBy) params.sortBy = filters.sortBy;
    if (filters?.sortOrder) params.sortOrder = filters.sortOrder;

    return apiGet<StudentProfile[]>(`/academies/${academyId}/students`, {
      params: Object.keys(params).length ? params : undefined,
    });
  },

  async getStudentProfile(
    academyId: string,
    studentId: string
  ): Promise<ApiResponse<StudentProfile>> {
    return apiGet<StudentProfile>(`/academies/${academyId}/students/${studentId}`);
  },

  async getAttendanceStats(academyId: string): Promise<ApiResponse<{
    totalStudents: number;
    averageAttendanceRate: number;
    perfectAttendance: number;
    atRisk: number;
  }>> {
    return apiGet(`/academies/${academyId}/students/stats`);
  },

  async getAttendanceExport(
    academyId: string,
    scope?: AttendanceExportScope
  ): Promise<ApiResponse<AttendanceHistoryExportRow[]>> {
    const params: Record<string, string> = {};
    if (scope?.termId) params.termId = scope.termId;
    if (scope?.lessonId) params.lessonId = scope.lessonId;
    if (scope?.category) params.category = scope.category;
    return apiGet<AttendanceHistoryExportRow[]>(`/academies/${academyId}/students/attendance-export`, {
      params: Object.keys(params).length ? params : undefined,
    });
  },

  async getStudentBillingReport(
    academyId: string,
    options: AttendanceExportScope & {
      studentId?: string;
      lessonId?: string;
      detailed?: boolean;
      periodStart?: string;
      periodEnd?: string;
      billByAttendance?: boolean;
      page?: number;
      limit?: number;
    }
  ): Promise<ApiResponse<StudentBillingReportResult>> {
    const params: Record<string, string | number> = {};
    if (options.termId) params.termId = options.termId;
    if (options.lessonId) params.lessonId = options.lessonId;
    if (options.category) params.category = options.category;
    if (options.studentId) params.studentId = options.studentId;
    if (options.detailed) params.detailed = 'true';
    if (options.periodStart) params.periodStart = options.periodStart;
    if (options.periodEnd) params.periodEnd = options.periodEnd;
    if (options.billByAttendance) params.billByAttendance = 'true';
    if (options.page !== undefined) params.page = options.page;
    if (options.limit !== undefined) params.limit = options.limit;
    return apiGet<StudentBillingReportResult>(`/academies/${academyId}/students/billing-report`, {
      params: Object.keys(params).length ? params : undefined,
    });
  },

  async exportStudentBillingReportCsv(
    academyId: string,
    options: AttendanceExportScope & {
      periodStart?: string;
      periodEnd?: string;
      billByAttendance?: boolean;
    }
  ): Promise<globalThis.Blob> {
    const params: Record<string, string> = { format: 'csv' };
    if (options.termId) params.termId = options.termId;
    if (options.lessonId) params.lessonId = options.lessonId;
    if (options.category) params.category = options.category;
    if (options.periodStart) params.periodStart = options.periodStart;
    if (options.periodEnd) params.periodEnd = options.periodEnd;
    if (options.billByAttendance) params.billByAttendance = 'true';
    const response = await apiClient.get(`/academies/${academyId}/students/billing-report`, {
      params,
      responseType: 'blob',
    });
    const blob = response.data as globalThis.Blob;
    const contentType = String(response.headers['content-type'] ?? '');
    if (!contentType.includes('text/csv')) {
      const text = await blob.text();
      let message = 'Failed to export billing report';
      try {
        const json = JSON.parse(text) as { error?: string };
        if (json.error) message = json.error;
      } catch {
        if (text.trim()) message = text.trim();
      }
      throw new Error(message);
    }
    return blob;
  },

  async importStudents(
    academyId: string,
    rows: Array<{
      firstName: string;
      lastName: string;
      dateOfBirth?: string;
      gender: 'male' | 'female' | 'other';
      skillLevel?: 'beginner' | 'intermediate' | 'advanced';
      medicalNotes?: string;
      guardianEmail?: string;
      inviteEmail?: string;
      emergencyContactName?: string;
      emergencyContactPhone?: string;
      emergencyContactRelationship?: string;
    }>
  ): Promise<ApiResponse<{
    createdCount: number;
    failedCount: number;
    results: Array<{ rowNumber: number; success: boolean; error?: string; studentId?: string }>;
  }>> {
    return apiPost(`/academies/${academyId}/students/import`, rows);
  },

  async fetchAllAcademyStudentsPaginated(
    academyId: string,
    filters?: StudentFilters,
    pageSize = 100
  ): Promise<StudentProfile[]> {
    const allStudents: StudentProfile[] = [];
    let page = 1;
    let totalPages = 1;

    while (page <= totalPages) {
      const response = await this.getAcademyStudentsPaginated(academyId, page, pageSize, filters);
      allStudents.push(...(response.data ?? []));
      totalPages = response.pagination?.totalPages ?? 1;
      page += 1;
    }

    return allStudents;
  },

  async getAcademyStudentsPaginated(
    academyId: string,
    page: number,
    limit: number,
    filters?: StudentFilters
  ): Promise<{ data: StudentProfile[]; pagination: PaginationState }> {
    const params: Record<string, string | number | boolean> = { page, limit };
    if (filters?.skillLevel) params.skillLevel = filters.skillLevel;
    if (filters?.listRecencyActive !== undefined) {
      params.listRecencyActive = filters.listRecencyActive;
    }
    if (filters?.ageMin !== undefined) params.ageMin = filters.ageMin;
    if (filters?.ageMax !== undefined) params.ageMax = filters.ageMax;
    if (filters?.searchQuery) params.searchQuery = filters.searchQuery;
    if (filters?.sortBy) params.sortBy = filters.sortBy;
    if (filters?.sortOrder) params.sortOrder = filters.sortOrder;
    if (filters?.ageGroupId) params.ageGroupId = filters.ageGroupId;
    if (filters?.ballColor) params.ballColor = filters.ballColor;
    if (filters?.programId) params.programId = filters.programId;
    if (filters?.studentIds?.length) params.studentIds = filters.studentIds.join(',');
    if (filters?.sessionId) params.sessionId = filters.sessionId;

    const response = await apiClient.get<{ data: StudentProfile[]; pagination: PaginationState }>(
      `/academies/${academyId}/students`,
      { params }
    );
    return response.data;
  },

  async getStudentLessons(
    academyId: string,
    studentId: string
  ): Promise<StudentLessonsByEnrollmentResponse> {
    const response = await apiGet<StudentLessonsByEnrollmentResponse>(
      `/academies/${academyId}/students/${studentId}/lessons`
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to load lessons');
  },

  async getStudentSchedule(academyId: string, studentId: string): Promise<ScheduleResponse> {
    const response = await apiGet<ScheduleResponse>(
      `/academies/${academyId}/students/${studentId}/schedule`
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to load student schedule');
  },

  async getStudentSiblings(
    academyId: string,
    studentId: string
  ): Promise<StudentSiblingSummary[]> {
    const response = await apiGet<StudentSiblingSummary[]>(
      `/academies/${academyId}/students/${studentId}/siblings`
    );
    if (response.success && response.data) {
      return response.data;
    }
    return [];
  },

  async getStudentEnrolledLessonIds(academyId: string, studentId: string): Promise<string[]> {
    const response = await apiGet<{ lessonIds: string[] }>(
      `/academies/${academyId}/students/${studentId}/lesson-ids`
    );
    if (response.success && response.data?.lessonIds) {
      return response.data.lessonIds;
    }
    throw new Error(response.error || 'Failed to load lesson ids');
  },

  async notifyStudents(
    academyId: string,
    body: {
      studentIds?: string[];
      sessionId?: string;
      subject: string;
      message: string;
      channels: { inApp: boolean; email: boolean; sms?: boolean };
    }
  ): Promise<
    ApiResponse<{
      messageLogId: string;
      sentInApp: number;
      sentEmail: number;
      sentSms: number;
      skipped: number;
    }>
  > {
    return apiPost(`/academies/${academyId}/students/notify`, body);
  },

  async getStudentMessageHistory(
    academyId: string,
    page = 1,
    limit = 20
  ): Promise<
    ApiResponse<
      Array<{
        id: string;
        subject: string;
        body: string;
        channels: { inApp: boolean; email: boolean; sms: boolean };
        targetKind: 'student_ids' | 'session';
        targetSessionId: string | null;
        targetStudentCount: number;
        targetSummary: Record<string, unknown> | null;
        sentInApp: number;
        sentEmail: number;
        sentSms: number;
        skipped: number;
        sentAt: string;
        senderName?: string;
      }>
    > & { pagination?: PaginationState }
  > {
    const response = await apiClient.get(`/academies/${academyId}/student-messages`, {
      params: { page, limit },
    });
    return response.data;
  },

  async getStudentMessageDetail(
    academyId: string,
    logId: string
  ): Promise<
    ApiResponse<{
      id: string;
      subject: string;
      body: string;
      channels: { inApp: boolean; email: boolean; sms: boolean };
      targetKind: 'student_ids' | 'session';
      targetSessionId: string | null;
      targetStudentCount: number;
      targetSummary: Record<string, unknown> | null;
      sentInApp: number;
      sentEmail: number;
      sentSms: number;
      skipped: number;
      sentAt: string;
      senderName?: string;
      recipients: Array<{
        studentId: string;
        name: string;
        recipientKind: 'self_student' | 'guardian' | 'invite_only';
        contact?: string;
      }>;
    }>
  > {
    return apiGet(`/academies/${academyId}/student-messages/${logId}`);
  },

  async createManualStudent(
    academyId: string,
    body: {
      firstName: string;
      lastName: string;
      dateOfBirth?: string;
      gender: 'male' | 'female' | 'other';
      skillLevel?: 'beginner' | 'intermediate' | 'advanced';
      inviteEmail?: string;
      invitePhone?: string;
    }
  ): Promise<ApiResponse<{ id: string }>> {
    return apiPost(`/academies/${academyId}/students/manual`, body);
  },

  async updateAcademyStudent(
    academyId: string,
    studentId: string,
    body: {
      firstName?: string;
      lastName?: string;
      dateOfBirth?: string | null;
      gender?: 'male' | 'female' | 'other';
      skillLevel?: 'beginner' | 'intermediate' | 'advanced' | '';
      medicalNotes?: string | null;
      emergencyContact?: {
        name: string;
        phone: string;
        relationship: string;
        email?: string;
      } | null;
      inviteEmail?: string | null;
      invitePhone?: string | null;
    }
  ): Promise<ApiResponse<StudentProfile>> {
    return apiPatch(`/academies/${academyId}/students/${studentId}`, body);
  },

  async sendGuardianInvite(
    academyId: string,
    studentId: string,
    options?: { lessonId?: string }
  ): Promise<ApiResponse<{ sent: boolean }>> {
    return apiPost(`/academies/${academyId}/students/${studentId}/send-guardian-invite`, {
      ...(options?.lessonId ? { lessonId: options.lessonId } : {}),
    });
  },

  async deleteAcademyStudent(
    academyId: string,
    studentId: string
  ): Promise<ApiResponse<{ id: string }>> {
    return apiDelete(`/academies/${academyId}/students/${studentId}`);
  },
};
